import React, { useEffect, useRef, useState } from "react";
import { RefreshCw, Send } from "lucide-react";
import { Button } from "../shadcn/components/ui/button";
import LoadingButton from "./Common/LoadingButton";
import StatusAlert from "./Common/StatusAlert";
import LabeledTextField from "./Common/LabeledTextField";
import firebase from "../auth/firebase";
import {
  confirmMobileVerification,
  getPendingMobileChange,
  initiateMobileChange,
  retryMobileSynchronization,
} from "../auth/mobileChange";
import { isExplicitlyImpersonating } from "../auth/emailChange";
import { useTranslation } from "../context/Internationalization";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../shadcn/components/ui/dialog";

export const MOBILE_RECAPTCHA_CONTAINER_ID = "mobile-change-recaptcha";

const readableErrorKey = (error) => {
  const value = `${error?.code || ""} ${error?.message || ""}`.toLowerCase();
  if (value.includes("impersonat"))
    return "mobile_change_impersonation_forbidden";
  if (value.includes("invalid_mobile")) return "mobile_change_invalid";
  if (value.includes("invalid-verification-code"))
    return "mobile_change_invalid_code";
  if (value.includes("code-expired") || value.includes("session-expired"))
    return "mobile_change_code_expired";
  if (value.includes("too-many-requests"))
    return "mobile_change_too_many_attempts";
  if (
    value.includes("credential-already-in-use") ||
    value.includes("phone-number-already-exists") ||
    value.includes("already in use")
  )
    return "mobile_change_already_in_use";
  if (value.includes("requires-recent-login"))
    return "mobile_change_recent_login_required";
  if (value.includes("account_mismatch") || value.includes("auth_required"))
    return "mobile_change_account_changed";
  if (value.includes("sync")) return "mobile_change_sync_pending";
  return "mobile_change_failed";
};

const MobileChangeModal = ({
  open,
  onClose,
  user,
  axiosGraphQL,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [request, setRequest] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const verifierRef = useRef(null);
  const inFlightRef = useRef(false);
  const impersonating = isExplicitlyImpersonating();
  const pendingSync = getPendingMobileChange();

  const acquireInFlight = () => {
    if (inFlightRef.current) return false;
    inFlightRef.current = true;
    return true;
  };

  const releaseInFlight = () => {
    inFlightRef.current = false;
  };

  const clearVerifier = () => {
    verifierRef.current?.clear?.();
    verifierRef.current = null;
  };

  useEffect(() => clearVerifier, []);

  const createVerifier = () => {
    clearVerifier();
    verifierRef.current = new firebase.auth.RecaptchaVerifier(
      MOBILE_RECAPTCHA_CONTAINER_ID,
      { size: "invisible" }
    );
    return verifierRef.current;
  };

  const finish = async (result) => {
    await onCompleted(result);
    setStatus("complete");
    clearVerifier();
  };

  const sendCode = async (event) => {
    event.preventDefault();
    if (status === "pending" || impersonating || !acquireInFlight()) return;
    setStatus("pending");
    setError("");
    try {
      setRequest(
        await initiateMobileChange({
          mobile,
          createVerifier,
          axiosGraphQL,
        })
      );
      setStatus("code");
    } catch (sendError) {
      clearVerifier();
      setError(t(readableErrorKey(sendError)));
      setStatus("idle");
    } finally {
      releaseInFlight();
    }
  };

  const confirmCode = async (event) => {
    event.preventDefault();
    if (status === "pending" || !request || !acquireInFlight()) return;
    setStatus("pending");
    setError("");
    try {
      await finish(
        await confirmMobileVerification({
          ...request,
          code,
          previousUser: user,
          axiosGraphQL,
        })
      );
    } catch (confirmError) {
      setError(t(readableErrorKey(confirmError)));
      setStatus(getPendingMobileChange() ? "sync" : "code");
    } finally {
      releaseInFlight();
    }
  };

  const retrySync = async () => {
    if (status === "pending" || impersonating || !acquireInFlight()) return;
    setStatus("pending");
    setError("");
    try {
      await finish(
        await retryMobileSynchronization({
          previousUser: user,
          axiosGraphQL,
        })
      );
    } catch (syncError) {
      setError(t(readableErrorKey(syncError)));
      setStatus("sync");
    } finally {
      releaseInFlight();
    }
  };

  const restartVerification = () => {
    clearVerifier();
    setCode("");
    setRequest(null);
    setStatus("idle");
    setError("");
  };

  const close = () => {
    if (status === "pending" || inFlightRef.current) return;
    clearVerifier();
    setMobile("");
    setCode("");
    setRequest(null);
    setStatus("idle");
    setError("");
    onClose();
  };

  const showSync = status === "sync" || (!request && pendingSync);
  const title = user?.mobile
    ? t("change_mobile_number")
    : t("add_mobile_number");

  return (
    <Dialog
      open={Boolean(open)}
      onOpenChange={(next, details) => !next && close(details?.event)}
    >
      <DialogContent closeLabel={t("close")}>
      <form className="contents" onSubmit={status === "code" ? confirmCode : sendCode}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex w-full flex-col gap-4">
            {status === "complete" ? (
              <StatusAlert severity="success">
                {t("mobile_change_verified")}
              </StatusAlert>
            ) : (
              <>
                {user?.mobile && (
                  <p className="text-sm">
                    {t("current_mobile")}: {user.mobile}
                  </p>
                )}
                {error && <StatusAlert severity="error">{error}</StatusAlert>}
                {impersonating && (
                  <StatusAlert severity="warning">
                    {t("mobile_change_impersonation_forbidden")}
                  </StatusAlert>
                )}
                {showSync ? (
                  <StatusAlert severity="warning">
                    {t("mobile_change_sync_retry_notice")}
                  </StatusAlert>
                ) : status === "code" || status === "pending" ? (
                  request && (
                    <LabeledTextField
                      autoFocus
                      required
                      label={t("sms_verification_code")}
                      value={code}
                      disabled={status === "pending"}
                      onChange={(event) => setCode(event.target.value)}
                    />
                  )
                ) : (
                  <LabeledTextField
                    autoFocus
                    required
                    label={t("mobile_number")}
                    helperText={t("mobile_international_format_helper")}
                    value={mobile}
                    disabled={status === "pending" || impersonating}
                    onChange={(event) => setMobile(event.target.value)}
                  />
                )}
                <div id={MOBILE_RECAPTCHA_CONTAINER_ID} />
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            {status === "complete" ? t("close") : t("cancel")}
          </Button>
          {status === "code" && (
            <Button
              type="button"
              variant="outline"
              onClick={restartVerification}
            >
              <Send aria-hidden="true" />
              {t("send_new_code")}
            </Button>
          )}
          {showSync ? (
            <LoadingButton
              type="button"
              icon={<RefreshCw aria-hidden="true" />}
              loading={status === "pending"}
              disabled={impersonating}
              onClick={retrySync}
            >
              {t("retry_synchronization")}
            </LoadingButton>
          ) : (
            status !== "complete" && (
              <LoadingButton
                icon={
                  status === "code" || request ? null : (
                    <Send aria-hidden="true" />
                  )
                }
                type="submit"
                loading={status === "pending"}
                disabled={impersonating}
              >
                {status === "code" || request
                  ? t("verify_code")
                  : t("send_sms_code")}
              </LoadingButton>
            )
          )}
        </DialogFooter>
      </form>
      </DialogContent>
    </Dialog>
  );
};

export default MobileChangeModal;
